import { languageMap } from "./constants";

export const extractPercentage = (str: string): number => {
  const defaultPercentage = 5;
  if (!str || typeof str !== "string") return defaultPercentage;

  const match = str.match(/(\d+)%/);
  return match ? parseInt(match[1], 10) : defaultPercentage;
};

export const checkIfBrowser = () => typeof window !== "undefined";

export const getStoreName = (domain: string): string => {
  const parts = domain.split(".");
  return parts[0];
};

type LanguageType = keyof typeof languageMap;

export const getLanguageCode = (languageName: string) => {
  if (!languageName || !Object.keys(languageMap).includes(languageName)) {
    return "";
  }

  return languageMap[languageName as LanguageType];
};

export const addCommasToNumber = (num: number) => {
  if (
    !num ||
    typeof num !== "number" ||
    isNaN(num) ||
    num === Infinity ||
    num === -Infinity
  )
    return "0";
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const resizeArray = (
  inputArray: number[],
  newSize: number,
): number[] => {
  const step = Math.floor(inputArray.length / newSize);
  if (inputArray.length % newSize !== 0) {
    throw new Error("Size of input array must be a multiple of new size");
  }
  const outputArray: number[] = [];
  for (let i = 0; i < inputArray.length; i += step) {
    const sum = inputArray.slice(i, i + step).reduce((a, b) => a + b, 0);
    outputArray.push(sum);
  }
  return outputArray;
};

export const buildQueryString = (params: { [key: string]: any }): string => {
  return Object.keys(params)
    .map((key) => {
      const value = params[key];
      if (value === undefined || value === null) {
        return ""; // Skip key-value pair if value is undefined or null
      }
      return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
    })
    .filter((param) => param !== "") // Filter out empty strings
    .join("&");
};

export const generateNumberFromId = (id: string): number => {
  // Calculate a hash value from the ID
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0; // Convert to 32-bit integer
  }

  // Map the hash value to a number in the range of 1 to 6
  const numberInRange = (Math.abs(hash) % 6) + 1;

  return numberInRange;
};

export const getShopifyCustomerId = (customerId: string) => {
  return customerId.split("shopify-")[1];
};

export const getShopName = (shopifyAppSettingsId: string) => {
  const parts = shopifyAppSettingsId.split(".");
  if (parts.length > 0) {
    return parts[0];
  }
  return "";
};

export const isValidNumber = (value: number): boolean => {
  return typeof value === "number" && isFinite(value);
};

export const cleanWebsiteURL = (url: string): string => {
  // Extract hostname from URL
  const hostname = new URL(url).hostname;
  // Remove 'www.' if present and return the cleaned URL
  return hostname.replace(/^www\./, "");
};

export const validateApiKey = (apiKey: string) => {
  return (
    apiKey.trim().length > 0 &&
    !apiKey.includes(" ") &&
    apiKey.length > 0 &&
    apiKey.length < 100
  );
};

export const twoDecimals = (input: number | string) => {
  const n = typeof input === "string" ? parseFloat(input) : input;

  if (isNaN(n)) return 0;

  const log10 = n ? Math.floor(Math.log10(n)) : 0,
    div = log10 < 0 ? Math.pow(10, 1 - log10) : 100;

  return Math.round(n * div) / div;
};

export const isLocalEnvironment = () => {
  try {
    let isLocal = false;

    if (typeof window !== "undefined" && window?.IS_LOCAL === "true") {
      isLocal = true;
    } else if (
      typeof process !== "undefined" &&
      (process.env.NODE_ENV === "development" ||
        process.env.IS_TEST_ENVIRONMENT === "true")
    ) {
      isLocal = true;
    }

    return isLocal;
  } catch (e) {
    return false;
  }
};

export const logIfLocal = (message?: any, ...optionalParams: any[]) => {
  if (isLocalEnvironment()) {
    console.log(message, ...optionalParams);
  }
};

export const formatDateToShortLocaleString = (date: string | number | Date) => {
  const dateString = new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return dateString;
};

export const parseDateToISO = (dateString: string) => {
  if (!dateString) return null;

  const date = new Date(dateString);

  if (!isNaN(date.getTime())) {
    return date.toISOString();
  } else {
    return null;
  }
};
